import { AblyProvider as AblyReactProvider } from "ably/react";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from "react";
import { destroyClient, initializeClient } from "../core/client";
import { configureAbly, noopLogger } from "../core/config";
import type { AblyLogger, ToxiproxyConfig } from "../core/types";

// ── Context for logger + apiKey ─────────────────────────────────────

type AblyConfigContextValue = {
	logger: AblyLogger;
	apiKey?: string;
};

const AblyConfigContext = createContext<AblyConfigContextValue>({ logger: noopLogger });

export function useAblyConfig() {
	return useContext(AblyConfigContext);
}

// ── Provider ────────────────────────────────────────────────────────

type ExternalAblyClient = React.ComponentProps<typeof AblyReactProvider>["client"];

type BaseAblyProviderProps = {
	children: ReactNode;
	logger?: AblyLogger;
};

type ManagedAblyProviderProps = BaseAblyProviderProps & {
	apiKey: string;
	clientId?: string;
	toxiproxy?: ToxiproxyConfig;
	client?: never;
};

type ExternalAblyProviderProps = BaseAblyProviderProps & {
	client: ExternalAblyClient;
	apiKey?: string;
	clientId?: never;
	toxiproxy?: never;
};

export type AblyProviderProps = ManagedAblyProviderProps | ExternalAblyProviderProps;

export default function AblyProvider(props: AblyProviderProps) {
	const { children, logger: providedLogger } = props;

	const logger = providedLogger ?? noopLogger;
	const managedApiKey = "apiKey" in props ? props.apiKey : undefined;
	const managedClientId = "clientId" in props ? props.clientId : undefined;
	const toxiproxy = "toxiproxy" in props ? props.toxiproxy : undefined;
	const externalClient = "client" in props ? props.client : undefined;
	const isManagedClient = !externalClient;

	// Keep global logger in sync for non-hook code (native messaging etc.)
	if (providedLogger) {
		configureAbly({ logger: providedLogger });
	}

	const client = useMemo(() => {
		if (externalClient) {
			return externalClient;
		}

		return initializeClient({
			key: managedApiKey!,
			clientId: managedClientId,
			toxiproxy,
			logger,
		});
	}, [externalClient, managedApiKey, managedClientId, toxiproxy, logger]);

	const disconnectedAt = useRef<number | null>(null);
	useEffect(() => {
		client?.connection.on((stateChange) => {
			const { current, previous, reason, retryIn } = stateChange;

			if (current === "disconnected" || current === "suspended") {
				if (!disconnectedAt.current) {
					disconnectedAt.current = Date.now();
				}
				logger.warning("INFO", `Ably connection ${current}`, {
					reason: reason?.message,
					retryIn,
				});
			} else if (current === "connected") {
				if (disconnectedAt.current) {
					const downtimeMs = Date.now() - disconnectedAt.current;
					logger.info("INFO", "Ably connection recovered", {
						downtime_ms: downtimeMs,
						previous_state: previous,
					});
					disconnectedAt.current = null;
				} else {
					logger.info("INFO", "Ably connection established", { previous });
				}
			} else if (current === "failed") {
				logger.warning("INFO", "Ably connection failed", {
					reason: reason?.message,
				});
			}
		});

		return () => {
			client?.connection.off();
			if (isManagedClient) {
				destroyClient();
			}
		};
	}, [client, isManagedClient, logger]);

	const configValue = useMemo<AblyConfigContextValue>(
		() => ({ logger, apiKey: managedApiKey }),
		[logger, managedApiKey],
	);

	return (
		<AblyConfigContext.Provider value={configValue}>
			<AblyReactProvider client={client}>{children}</AblyReactProvider>
		</AblyConfigContext.Provider>
	);
}
